/**
 * Google OAuth Authentication Service
 * Handles Google Sign-In integration with backend API
 */

import axios from 'axios';

const API_BASE_URL = (process.env.VUE_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

class GoogleAuthService {
  constructor() {
    this.isInitialized = false;
    this.googleClientId = null;
  }

  /**
   * Initialize Google OAuth
   * @param {string} clientId - Google OAuth Client ID
   */
  async initialize(clientId) {
    if (this.isInitialized) return;

    try {
      this.googleClientId = clientId;
      
      // Load Google Identity Services script
      await this.loadGoogleScript();
      
      // Initialize Google OAuth
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: this.handleCredentialResponse.bind(this),
          auto_select: false,
          cancel_on_tap_outside: true
        });
        
        this.isInitialized = true;
      } else {
        throw new Error('Google Identity Services not loaded');
      }
    } catch (error) {
      console.error('Failed to initialize Google OAuth:', error);
      throw error;
    }
  }

  /**
   * Load Google Identity Services script
   */
  loadGoogleScript() {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.accounts) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = resolve;
      script.onerror = reject;
      
      document.head.appendChild(script);
    });
  }

  /**
   * Handle Google credential response (callback)
   * @param {Object} response - Google credential response
   */
  handleCredentialResponse(response) {
    // This is used for automatic sign-in flow
    // For manual sign-in, we'll handle tokens differently
    // (no-op in production logs)
  }

  /**
   * Show Google Sign-In popup
   * @param {string} role - User role (student, teacher, parent)
   * @returns {Promise} - Promise that resolves with user data
   */
  async signInWithPopup(role) {
    if (!this.isInitialized) {
      throw new Error('Google OAuth not initialized');
    }

    try {
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Sign-in timeout - please try again')), 60000); // 60 seconds
      });

      // Create a promise that resolves when user completes sign-in
      const googleToken = await Promise.race([
        this.getGoogleToken(),
        timeoutPromise
      ]);
      
      if (!googleToken) {
        throw new Error('No Google token received');
      }
      
      // Send token to backend for verification and user creation/login
      const response = await this.authenticateWithBackend(googleToken, role);
      
      return response;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }

  /**
   * Get Google token using popup (simplified approach)
   * @returns {Promise<string>} - Google ID token
   */
  getGoogleToken() {
    return new Promise((resolve, reject) => {
      try {
        if (!window.google || !window.google.accounts) {
          reject(new Error('Google Identity Services not loaded'));
          return;
        }

        

        // Initialize Google Sign-In with callback
        window.google.accounts.id.initialize({
          client_id: this.googleClientId,
          callback: (response) => {
            
            if (response.credential) {
              resolve(response.credential);
            } else {
              reject(new Error('No credential received from Google'));
            }
          },
          auto_select: false,
          cancel_on_tap_outside: false
        });

        // Show the One Tap prompt
        window.google.accounts.id.prompt((notification) => {
          
          if (notification.isNotDisplayed()) {
            
            // If One Tap fails, use the OAuth popup
            this.showGooglePopup().then(resolve).catch(reject);
          }
          if (notification.isSkippedMoment()) {
            
            this.showGooglePopup().then(resolve).catch(reject);
          }
        });

      } catch (error) {
        console.error('Google token error:', error);
        reject(error);
      }
    });
  }

  /**
   * Handle manual sign-in when One Tap is not available
   * @returns {Promise<string>} - Google ID token
   */
  handleManualSignIn() {
    return new Promise((resolve, reject) => {
      try {
        // Create a hidden button for programmatic triggering
        const buttonDiv = document.createElement('div');
        buttonDiv.style.display = 'none';
        document.body.appendChild(buttonDiv);

        window.google.accounts.id.renderButton(buttonDiv, {
          theme: 'outline',
          size: 'large',
          width: '100%'
        });

        // Override the callback to capture the token
        window.google.accounts.id.initialize({
          client_id: this.googleClientId,
          callback: (response) => {
            document.body.removeChild(buttonDiv);
            if (response.credential) {
              resolve(response.credential);
            } else {
              reject(new Error('No credential received'));
            }
          },
          auto_select: false
        });

        // Trigger the button click
        const button = buttonDiv.querySelector('div[role="button"]');
        if (button) {
          button.click();
        } else {
          // Fallback: prompt for One Tap
          window.google.accounts.id.prompt();
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Show Google popup for sign-in
   * @returns {Promise<string>} - Google ID token
   */
  showGooglePopup() {
    return new Promise((resolve, reject) => {
      try {
        // Use Google Identity Services prompt
        window.google.accounts.id.prompt((notification) => {
          if (notification.isDisplayed()) {
            // One Tap is displayed
            return;
          }
          if (notification.isNotDisplayed()) {
            reject(new Error('One Tap could not be displayed'));
          }
          if (notification.isSkippedMoment()) {
            reject(new Error('One Tap was skipped'));
          }
        });

        // Set up callback to capture the token
        window.google.accounts.id.initialize({
          client_id: this.googleClientId,
          callback: (response) => {
            if (response.credential) {
              resolve(response.credential);
            } else {
              reject(new Error('No credential received'));
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Authenticate with backend using Google token
   * @param {string} token - Google ID token
   * @param {string} role - User role
   * @returns {Promise<Object>} - Backend response
   */
  async authenticateWithBackend(token, role) {
    try {
      
      const response = await axios.post(`${API_BASE_URL}/api/auth/google`, {
        token: token,
        role: role
      }, {
        timeout: 30000 // 30 second timeout
      });

      
      return response.data;
    } catch (error) {
      console.error('Backend authentication error:', error);
      
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Authentication failed');
      }
      if (error.code === 'ECONNABORTED') {
        throw new Error('Connection timeout - please try again');
      }
      throw error;
    }
  }

  /**
   * Verify email with backend
   * @param {string} token - Verification token
   * @returns {Promise<Object>} - Backend response
   */
  async verifyEmail(token) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/google/verify-email`, {
        token: token
      });

      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Email verification failed');
      }
      throw error;
    }
  }

  /**
   * Resend verification email
   * @param {string} email - User email
   * @param {string} role - User role
   * @returns {Promise<Object>} - Backend response
   */
  async resendVerificationEmail(email, role) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/google/resend-verification`, {
        email: email,
        role: role
      });

      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Failed to resend verification email');
      }
      throw error;
    }
  }

  /**
   * Link Google account to existing user
   * @param {string} token - Google ID token
   * @param {string} authToken - User's auth token
   * @returns {Promise<Object>} - Backend response
   */
  async linkAccount(token, authToken) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/google/link-account`, {
        token: token
      }, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Failed to link account');
      }
      throw error;
    }
  }

  /**
   * Unlink Google account from current user
   * @param {string} authToken - User's auth token
   * @returns {Promise<Object>} - Backend response
   */
  async unlinkAccount(authToken) {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/google/unlink-account`, {}, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Failed to unlink account');
      }
      throw error;
    }
  }

  /**
   * Sign out from Google
   */
  async signOut() {
    try {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.disableAutoSelect();
      }
    } catch (error) {
      console.error('Google sign-out error:', error);
    }
  }

  /**
   * Render Google Sign-In button
   * @param {HTMLElement} element - DOM element to render button in
   * @param {Object} options - Button options
   */
  renderButton(element, options = {}) {
    if (!this.isInitialized) {
      throw new Error('Google OAuth not initialized');
    }

    const defaultOptions = {
      theme: 'outline',
      size: 'large',
      width: '100%',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left'
    };

    const buttonOptions = { ...defaultOptions, ...options };

    try {
      window.google.accounts.id.renderButton(element, buttonOptions);
    } catch (error) {
      console.error('Failed to render Google button:', error);
      throw error;
    }
  }

  /**
   * Check if user is signed in with Google
   * @returns {boolean} - True if signed in
   */
  isSignedIn() {
    // This would need to be implemented based on your app's state management
    return false;
  }
}

// Export singleton instance
export default new GoogleAuthService(); 